/**
 * NOTE: Lobby-initiated call comments are handled
 * differently than queue or normal call reports.
 *
 * - Lobby: after submit, route back to lobby/main screen.
 * - Queue (waiting): after submit, route to
 *   continue/leave queue screen.
 *
 * This logic is split here, but could be unified
 * in a future refactor.
 */
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useCreateCallComments } from 'features/call/call-comments/hooks';
import type { CallCommentsFormValues } from 'features/call/call-comments/interfaces';
import { ReportType } from 'features/call/vrs-call-comments/enums';
import { callCommentsFormSubmitAnalytics } from 'features/call/vrs-call-comments/helpers';
import {
  resetCallComments,
  selectIsPreviousCallReportStartedFromLobby,
  setReportType,
} from 'features/call/vrs-call-comments/store/';
import { handleError } from 'features/notification/store';
import { PageRoute } from 'features/router/routes';

export const useVrsCallCommentsFormSubmit = () => {
  const dispatch = useAppDispatch();
  const { createComment } = useCreateCallComments();
  const isPreviousCallReportStartedFromLobby = useAppSelector(
    selectIsPreviousCallReportStartedFromLobby
  );
  const navigate = useNavigate();

  const submitLobbyCallComments = async (
    formValues: CallCommentsFormValues
  ) => {
    try {
      await createComment(formValues);
      dispatch(
        callCommentsFormSubmitAnalytics(
          'onPreviousCallCommentsFormSubmit',
          formValues.commentTypes
        )
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'onPreviousCallCommentsFormSubmit',
        })
      );
    } finally {
      navigate(PageRoute.MAIN, { replace: true });
      dispatch(resetCallComments());
    }
  };

  const submitRegularCallComments = async (
    formValues: CallCommentsFormValues
  ) => {
    try {
      await createComment(formValues);
      dispatch(
        callCommentsFormSubmitAnalytics(
          'onCallCommentsFormSubmit',
          formValues.commentTypes
        )
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'onCallCommentsFormSubmit',
        })
      );
    } finally {
      dispatch(setReportType(ReportType.END));
    }
  };

  return isPreviousCallReportStartedFromLobby
    ? { submitCallComments: submitLobbyCallComments }
    : { submitCallComments: submitRegularCallComments };
};
